import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";
import { ProjectStatusPill, statusLabel } from "./ProjectForm";

const ALL = "";
const STATUSES = ["active", "on_hold", "completed", "inactive"];

// The API sends "-" for an unset customer or cost center.
const isBlank = (value) => !value || value === "-";

const today = () => new Date().toLocaleDateString("en-CA");

// Still marked active after its end date has passed.
const isOverdue = (row) =>
  row.status === "active" && row.end_date && row.end_date < today();

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => setAlert(null), 4000);
  };

  // The customer filter is applied server-side, so it matches the counts shown
  // on the customer list and detail screens.
  const fetchData = async (customer_id = customerId) => {
    setLoading(true);

    try {
      const res = await Http.get(
        "/acc-project" + (customer_id ? "?customer_id=" + customer_id : ""),
      );
      setReceiveData(
        (res.data || []).map((item) => ({
          ...item,
          budget_amount_display: money(item.budget_amount),
          status_display: statusLabel(item.status),
        })),
      );
    } catch (error) {
      console.error("Fetch error:", error);
      showAlert(
        "Could not load projects. Please refresh and try again.",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(ALL);

    Http.get("/acc-customer-select2")
      .then((res) => setCustomers(res.data || []))
      .catch((e) => console.error("Fetch error:", e));
  }, []);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const customerFilterChange = (e) => {
    setCustomerId(e.target.value);
    fetchData(e.target.value);
  };

  const deleteData = (item) => {
    Swal.fire({
      title: "Delete this project?",
      text: `"${item.code} - ${item.name}" will be removed permanently.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      Http.delete("/acc-project/" + item.id)
        .then((res) => {
          showAlert(res.data.message, res.data.type || "success");
          fetchData();
        })
        .catch((e) => {
          Swal.fire(
            "Not deleted",
            e.response?.data?.message ||
              "Could not delete the project. Please try again.",
            "warning",
          );
        });
    });
  };

  // Stats follow the customer filter (server-side) but not the status one,
  // so the status counts stay visible while a status is picked.
  const stats = useMemo(() => {
    const rows = receiveData || [];
    const count = (value) => rows.filter((row) => row.status === value).length;
    const live = rows.filter(
      (row) => row.status === "active" || row.status === "on_hold",
    );
    return {
      total: rows.length,
      active: count("active"),
      onHold: count("on_hold"),
      completed: count("completed"),
      inactive: count("inactive"),
      overdue: rows.filter(isOverdue).length,
      liveBudget: live.reduce((sum, row) => sum + num(row.budget_amount), 0),
      totalBudget: rows.reduce((sum, row) => sum + num(row.budget_amount), 0),
    };
  }, [receiveData]);

  const visibleRows = useMemo(
    () =>
      status === ALL
        ? receiveData
        : receiveData.filter((row) => row.status === status),
    [receiveData, status],
  );

  const codeBodyTemplate = (rowData) => (
    <span className="coa-code">{rowData.code}</span>
  );

  const nameBodyTemplate = (rowData) => (
    <span style={{ fontWeight: 600, color: "#2a2962" }}>{rowData.name}</span>
  );

  const textBodyTemplate = (field, emptyLabel = "—") => {
    const Component = (rowData) =>
      isBlank(rowData[field]) ? (
        <span style={{ color: "#b6bacb" }}>{emptyLabel}</span>
      ) : (
        <span>{rowData[field]}</span>
      );
    Component.displayName = `TextBodyTemplate(${field})`;
    return Component;
  };

  const dateBodyTemplate = (field) => {
    const Component = (rowData) => {
      const overdue = field === "end_date" && isOverdue(rowData);
      if (!rowData[field]) {
        return <span style={{ color: "#b6bacb" }}>—</span>;
      }
      return (
        <span
          style={{
            whiteSpace: "nowrap",
            color: overdue ? "#e42320" : undefined,
            fontWeight: overdue ? 600 : undefined,
          }}
          title={overdue ? "Past its end date but still active" : undefined}
        >
          {formatDate(rowData[field])}
          {overdue && <i className="fa fa-exclamation-circle mg-l-4"></i>}
        </span>
      );
    };
    Component.displayName = `DateBodyTemplate(${field})`;
    return Component;
  };

  const budgetBodyTemplate = (rowData) => {
    const value = num(rowData.budget_amount);
    return (
      <span
        className="coa-num"
        style={{
          display: "block",
          fontWeight: value ? 600 : 400,
          color: value ? "#2a2962" : "#b6bacb",
        }}
      >
        {money(value)}
      </span>
    );
  };

  const statusBodyTemplate = (rowData) => (
    <ProjectStatusPill status={rowData.status} />
  );

  const cols = [
    { field: "code", header: "Code", body: codeBodyTemplate },
    { field: "name", header: "Name", body: nameBodyTemplate },
    {
      field: "customer",
      header: "Customer",
      body: textBodyTemplate("customer", "Internal"),
    },
    {
      field: "cost_center",
      header: "Cost Center",
      body: textBodyTemplate("cost_center"),
    },
    {
      field: "start_date",
      header: "Start",
      body: dateBodyTemplate("start_date"),
    },
    { field: "end_date", header: "End", body: dateBodyTemplate("end_date") },
    {
      field: "budget_amount_display",
      sortField: "budget_amount",
      header: "Budget",
      body: budgetBodyTemplate,
      headerClassName: "coa-num",
    },
    // Not "Status"/" Status": those switch on ReactDataTable's own tags and
    // filters, which don't know completed or on_hold. The filter lives above.
    {
      field: "status_display",
      sortField: "status",
      header: "Project Status",
      body: statusBodyTemplate,
    },
  ];

  const selectedCustomer = customers.find(
    (customer) => String(customer.value) === String(customerId),
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Projects</h5>
            <p className="text-muted mb-0 tx-12">
              {selectedCustomer
                ? `Projects for ${selectedCustomer.label}`
                : "Work that costs are charged to, with its customer and cost center"}
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link
              to={
                "/create-acc-project" +
                (customerId ? "?customer_id=" + customerId : "")
              }
              className="btn btn-primary"
            >
              <i className="fa fa-plus-square mg-r-6"></i>New Project
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-briefcase coa-stat-icon"></i>
                <span className="coa-stat-label">Projects</span>
                <span className="coa-stat-value">{stats.total}</span>
                <span className="coa-stat-sub">
                  {stats.completed} completed · {stats.inactive} inactive
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat is-ok">
                <i className="fa fa-play-circle-o coa-stat-icon"></i>
                <span className="coa-stat-label">In Progress</span>
                <span className="coa-stat-value">{stats.active}</span>
                <span className="coa-stat-sub">{stats.onHold} on hold</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className={`coa-stat ${stats.overdue ? "is-alert" : ""}`}>
                <i className="fa fa-clock-o coa-stat-icon"></i>
                <span className="coa-stat-label">Past End Date</span>
                <span className="coa-stat-value">{stats.overdue}</span>
                <span className="coa-stat-sub">
                  {stats.overdue
                    ? "Still active after their end date"
                    : "Every active project is on schedule"}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-money coa-stat-icon"></i>
                <span className="coa-stat-label">Live Budget</span>
                <span className="coa-stat-value">
                  {money(stats.liveBudget)}
                </span>
                <span className="coa-stat-sub">
                  Active and on hold · {money(stats.totalBudget)} overall
                </span>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Filters
              </span>
              {(customerId || status) && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setStatus(ALL);
                    if (customerId) {
                      setCustomerId(ALL);
                      fetchData(ALL);
                    }
                  }}
                >
                  <i className="fa fa-times mg-r-6"></i>Clear filters
                </button>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row">
                <div className="col-lg-5">
                  <label>Customer</label>
                  <select
                    className="form-control"
                    value={customerId}
                    onChange={customerFilterChange}
                  >
                    <option value={ALL}>All customers</option>
                    {customers.map((customer) => (
                      <option key={customer.value} value={customer.value}>
                        {customer.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-7 mg-t-10 mg-lg-t-0">
                  <label>Status</label>
                  <div className="d-flex flex-wrap gap-2">
                    {[ALL, ...STATUSES].map((value) => {
                      const active = status === value;
                      const count =
                        value === ALL
                          ? stats.total
                          : receiveData.filter((row) => row.status === value)
                              .length;
                      return (
                        <button
                          key={value || "all"}
                          type="button"
                          onClick={() => setStatus(value)}
                          className={`coa-chip${active ? " is-active" : ""}`}
                          style={
                            active ? { backgroundColor: "#2a2962" } : undefined
                          }
                        >
                          {value === ALL ? "All" : statusLabel(value)}
                          <span className="coa-chip-count">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>
                {status ? `${statusLabel(status)} Projects` : "All Projects"}
              </span>
            </div>
            <div className="coa-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={visibleRows}
                isLoading={loading}
                actionTemplate={actionTemplate}
                deleteData={deleteData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item, deleteData) => {
  return (
    <div className="d-flex gap-1">
      <Link
        to={{ pathname: "/edit-acc-project/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Edit project"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      <button
        type="button"
        className="btn btn-outline-danger btn-sm"
        title="Delete project"
        onClick={() => deleteData(item)}
      >
        <i className="fa fa-trash" aria-hidden="true"></i>
      </button>
    </div>
  );
};

export default List;
